import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

interface PaginationMeta {
    total_count: number;
    returned_count: number;
    limit: number;
    offset: number;
    has_more: boolean;
}

interface SummaryStats {
    pending_reviews: number;
    approved: number;
}

export interface ReviewRecord {
    record_id: string;
    batch_id: string;
    status: string;
    assigned_to: string | null;
    total_files: number;
    processed_files: number;
    pending_review: number;
}

interface ValidatorReviewsResponse {
    pagination: PaginationMeta;
    summary: SummaryStats;
    reviews: ReviewRecord[];
}

interface ClassificationSummary {
    total_sections: number;
    critical: number;
    not_critical: number;
    validated_sections: number;
}

interface DocumentClassification {
    section_id: string;
    section_index: number;
    statementId: string;
    section_title: string;
    classification: string;
    classification_reason: string;
    confidence: number;
    validator_reason: string | null;
    validator_classification: string | null;
    validated_by: string | null;
    validated_at: string | null;
    validation_status: string;
    sectionimage_path: string;
    revision_statement: string;
}

export interface DocumentDetail {
    record_id: string;
    document_id: string;
    file_name: string;
    blob_path: string;
    status: string;
    classification_summary: ClassificationSummary;
    classifications: DocumentClassification[];
}

export interface RecordDetailResponse {
    record_id: string;
    batch_id: string;
    user_name: string;
    user_id: string;
    status: string;
    pagination: PaginationMeta;
    statementId: string;
    summary: {
        total_documents: number;
        pending_review: number;
        approved: number;
    };
    documents: DocumentDetail[];
}

export interface SectionImageItem {
    page: number;
    blob_path: string;
    url: string;
    size: number;
}

export interface SectionImagesResponse {
    record_id: string;
    document_id: string;
    section_index: number;
    section_title: string;
    sectionimage_path: string;
    total_images: number;
    images: SectionImageItem[];
}

export interface UpdateClassificationResponse {
    validations: {
        section?: number;
        status?: string;
        validator_classification?: string;
        reason?: string;
        statementId?: string;
        comments?: string;
    }[];
}

export interface UpdateClassificationRequest {
    validations: {
        status: string;
        validator_classification?: string;
        reason?: string;
        statementId: string;
    }[];
}

import { environment } from "@env/environment";

@Injectable({
    providedIn: 'root'
})
export class ValidatorService {
    // API URL is loaded from environment (environment.dev.ts / environment.prod.ts)
    // Dev URL  : https://sdsint-sds-web-dev.azurewebsites.net
    // Prod URL : https://sdsint-sds-web-dev.azurewebsites.net

    private readonly url: string = environment.apiUrl;
    private readonly apiPath = this.url + '/api/v1/validator';
    

    constructor(private readonly http: HttpClient) { }

    /**
     * Fetch reviews list with pagination and filtering
     * @param status Filter by status (e.g., 'pending_review', 'approved')
     * @param limit Maximum number of items to return (default: 50)
     * @param offset Number of items to skip (default: 0)
     * @returns Observable of ValidatorReviewsResponse
     */
    getReviews(
        status: string = 'pending_review',
        limit: number = 50,
        offset: number = 0
    ): Observable<ValidatorReviewsResponse> {
        const params = new HttpParams()
            .set('status', status.toString())
            .set('limit', limit.toString())
            .set('offset', offset.toString());

        return this.http.get<ValidatorReviewsResponse>(
            `${this.apiPath}/reviews`,
            { params }
        );
    }

    /**
     * Fetch record documents detail by record_id
     * @param recordId The record ID to fetch details for
     * @param limit Maximum number of documents to return (default: 10)
     * @param offset Number of documents to skip (default: 0)
     * @param classification Optional filter (critical, not-critical, all)
     * @returns Observable of RecordDetailResponse
     */
    getRecordDetail(
        recordId: string,
        limit: number = 10,
        offset: number = 0,
        classification?: string
    ): Observable<RecordDetailResponse> {
        let params = new HttpParams()
            .set('limit', limit.toString())
            .set('offset', offset.toString());

        if (classification) {
            params = params.set('classification', classification);
        }

        return this.http.get<RecordDetailResponse>(
            `${this.apiPath}/reviews/${recordId}/documents`,
            { params }
        );
    }

    getSectionImages(
        recordId: string,
        documentId: string,
        sectionIndex: number
    ): Observable<SectionImagesResponse> {
        return this.http.get<SectionImagesResponse>(
            `${this.apiPath}/record/${recordId}/documents/${documentId}/section/${sectionIndex}/images`
        );
    }

    /**
     * Update classification for a specific document in a record
     * @param queryParams - Object containing record_id and document_id
     * @param payload - UpdateClassificationResponse payload
     * @returns Observable of UpdateClassificationResponse
     */
    public updateClassification(
        queryParams: {
            record_id: string;
            document_id: string;
        }, payload: UpdateClassificationRequest
    ): Observable<UpdateClassificationResponse> {
        return this.http.post<UpdateClassificationResponse>(
            `${this.apiPath}/reviews/${queryParams.record_id}/documents/${queryParams.document_id}/validate`,
            payload
        );
    }

}
